import type { ComplianceSummary } from "../types";

function csvEscape(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Build a CSV of the heatmap. Excel opens this directly with a double
 * click. Columns:
 *   Name, Team, Type (Employee/Contractor), Email,
 *   <each week> -> "status", "submitted at", "month-end miss", "approval",
 *   fully-compliant % over the exported weeks, plus month-end / approval /
 *   hours miss totals over the same window.
 */
export function exportHeatmapCsv(summary: ComplianceSummary): void {
  const weeks = summary.heatmap.weeks;
  const header = [
    "Name",
    "Team",
    "Type",
    "Email",
    ...weeks.flatMap((w) => [
      `${w} status`,
      `${w} submitted at`,
      `${w} month-end miss`,
      `${w} approval`,
      `${w} hours`,
      `${w} hours logged`,
    ]),
    "Fully compliant % (exported weeks)",
    "Month-end misses (exported weeks)",
    "Approval misses (exported weeks)",
    "Hours misses (exported weeks)",
  ];

  const lines: string[] = [header.map(csvEscape).join(",")];

  for (const row of summary.heatmap.rows) {
    let onTimeCount = 0;
    let monthEndMissCount = 0;
    let approvalMissCount = 0;
    let hoursMissCount = 0;
    // Weeks where the person had no entries at all. Excluded from the
    // YTD % denominator below — contractors not engaged that week
    // shouldn't drag the number down.
    let noEntriesCount = 0;
    const cellCols: (string | number | null)[] = [];
    for (const cell of row.values) {
      cellCols.push(cell.label);
      cellCols.push(cell.submitted_at ?? "");
      cellCols.push(
        cell.month_end_pass === false
          ? "MISS"
          : cell.month_end_pass === true
          ? "ok"
          : "",
      );
      cellCols.push(
        cell.approval_pass === false
          ? "MISS"
          : cell.approval_pass === true
          ? "ok"
          : "",
      );
      cellCols.push(
        cell.hours_pass === false
          ? "MISS"
          : cell.hours_pass === true
          ? "ok"
          : "",
      );
      cellCols.push(cell.hours_logged ?? "");
      const fully =
        cell.label === "on_time" &&
        cell.month_end_pass !== false &&
        cell.approval_pass !== false &&
        cell.hours_pass !== false;
      if (fully) onTimeCount += 1;
      if (cell.month_end_pass === false) monthEndMissCount += 1;
      if (cell.approval_pass === false) approvalMissCount += 1;
      if (cell.hours_pass === false) hoursMissCount += 1;
      if (cell.label === "no_entries" || cell.label === "pending") noEntriesCount += 1;
    }
    const evaluatedWeeks = row.values.length - noEntriesCount;
    const ytdPct =
      evaluatedWeeks > 0
        ? Math.round((100 * onTimeCount) / evaluatedWeeks)
        : 0;
    lines.push(
      [
        row.name,
        row.team,
        (row.user_group || "").toLowerCase() === "contractor"
          ? "Contractor"
          : "Employee",
        row.email ?? "",
        ...cellCols,
        ytdPct,
        monthEndMissCount,
        approvalMissCount,
        hoursMissCount,
      ]
        .map(csvEscape)
        .join(","),
    );
  }

  // BOM so Excel detects UTF-8 correctly.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `timesheet-compliance-${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
